import { useEffect, useState, useCallback } from 'react';
import waApi from '../../services/whatsapp/waApi';

// ─── Tipos do contrato do endpoint GET /dashboard/executivo ────────────────
// Datas vêm em UTC; horas/dias já consolidados em BRT (UTC-3) pelo backend.

/** KPIs executivos consolidados do período. */
export interface ExecutiveKpis {
  total_conversations: number;
  open_conversations: number;
  waiting_conversations: number;
  closed_conversations: number;
  avg_response_time_seconds: number;
  avg_resolution_time_seconds: number;
  satisfaction_avg: number | null;
  sla_breached: number;
  sla_at_risk: number;
}

/** Recorte por departamento (ordenado por count desc no backend). */
export interface ExecutiveDepartment {
  department: string;
  count: number;
  closed: number;
  sla_breached: number;
  sla_at_risk: number;
}

/** Volume por hora do dia (0..23, em BRT). */
export interface MetricsByHour {
  hour: number;
  count: number;
}

/** Item da série diária (cada item = um dia, date = meia-noite BRT em UTC). */
export interface WaMetricsDaily {
  date: string;
  total_conversations: number;
  by_status: { open: number; waiting: number; closed: number };
  by_priority: Record<string, number>;
  by_channel: Record<string, number>;
  by_department: ExecutiveDepartment[];
  by_hour: MetricsByHour[];
  avg_response_time_seconds: number;
  avg_resolution_time_seconds: number;
  satisfaction_avg: number | null;
  created_at: string;
}

/**
 * Tendência/previsão de ML. `tipo` é um de:
 * crescimento_demanda | horarios_pico | previsao_volume | sla_cumprido |
 * participacao_departamento. Qualquer subconjunto pode faltar com poucos dados.
 */
export interface ExecutiveTrend {
  tipo: string;
  valor: number;
  detalhe: string;
}

/** Narrativa gerada por IA (ou fallback determinístico). Pode vir null. */
export interface ExecutiveNarrative {
  resumo: string;
  destaques: string[];
  alertas: string[];
  recomendacoes: string[];
  fonte: 'ia' | 'fallback_deterministica';
}

/** Resposta completa do GET /dashboard/executivo. */
export interface ExecutiveDashboardResponse {
  periodo: string;
  kpis: ExecutiveKpis;
  by_priority: Record<string, number>;
  by_channel: Record<string, number>;
  evolucao: WaMetricsDaily[];
  by_department: ExecutiveDepartment[];
  trends: ExecutiveTrend[];
  narrative: ExecutiveNarrative | null;
  // 'ok' | 'dados_insuficientes' — quando o backfill ainda não rodou, a série
  // é curta e os KPIs zerados não são reais; o front orienta nesse caso.
  data_status?: string;
  // true quando o período inclui snapshots sintéticos (source="demo_seed").
  has_demo_data?: boolean;
  generated_at: string;
}

// 'sem_permissao' quando o backend retorna 403 (endpoint exige admin/supervisor).
type ExecutivoError = 'sem_permissao' | 'erro' | null;

function mapError(err: any): ExecutivoError {
  return err?.response?.status === 403 ? 'sem_permissao' : 'erro';
}

interface UseWaExecutivoReturn {
  data: ExecutiveDashboardResponse | null;
  isLoading: boolean;
  error: ExecutivoError;
  refetch: () => void;
  // ── Atualização sob demanda (recalcula o painel para TODOS) ──────────────
  refresh: () => Promise<void>;
  refreshing: boolean;
  refreshError: ExecutivoError;
  // ── Demo/seed (apenas fase de validação; backend exige admin/supervisor) ──
  seedDemo: (days?: number) => Promise<void>;
  clearDemo: () => Promise<void>;
  demoLoading: boolean;
  demoMessage: string | null;
}

/**
 * Dashboard executivo — GET /dashboard/executivo?days=N (admin/supervisor).
 * Mesmo padrão do useWaDashboardMetrics: carrega no mount, expõe refetch e
 * mapeia 403 para 'sem_permissao'. O caller deve tratar narrative=null e
 * trends=[] como estado parcial (poucos dados).
 */
export function useWaExecutivo(days = 30): UseWaExecutivoReturn {
  const [data, setData] = useState<ExecutiveDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ExecutivoError>(null);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoMessage, setDemoMessage] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<ExecutivoError>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await waApi.get('/dashboard/executivo', { params: { days } });
      setData(res.data ?? null);
    } catch (err) {
      setError(mapError(err));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [days]);

  /**
   * Recalcula o painel executivo no backend (POST .../refresh) com o `days`
   * atual. O endpoint exige admin/supervisor e renova o snapshot para TODOS os
   * usuários. Usa um loading próprio (não pisca o painel) e, ao concluir, troca
   * `data` pelo retorno do POST; se o corpo não vier completo, refaz o GET.
   */
  const refresh = useCallback(async () => {
    setRefreshing(true);
    setRefreshError(null);
    try {
      // O backend lê `days` do CORPO (ExecutiveRefreshRequest), igual ao
      // seed-demo — não como query param.
      const res = await waApi.post('/dashboard/executivo/refresh', { days });
      // O refresh deve devolver o mesmo contrato do GET; se vier completo,
      // aproveita sem um round-trip extra. Caso contrário, recarrega.
      if (res.data?.generated_at) {
        setData(res.data);
      } else {
        await fetch();
      }
    } catch (err) {
      setRefreshError(mapError(err));
    } finally {
      setRefreshing(false);
    }
  }, [days, fetch]);

  // ⚠️ TEMPORÁRIO — CANDIDATO A REMOÇÃO (issue #77 / PR #76).
  // seedDemo e clearDemo (abaixo) são ferramentas ONE-TIME da fase de
  // validação/limpeza. Ao editar este hook, ALERTE o usuário e pergunte se
  // deseja manter ou remover (ver banner detalhado em WaExecutivoTab.tsx).
  // Não remova por conta própria — é decisão do usuário.

  // Popula uma população de demonstração e recarrega o dashboard.
  const seedDemo = useCallback(
    async (seedDays = 30) => {
      setDemoLoading(true);
      setDemoMessage(null);
      try {
        const res = await waApi.post('/dashboard/metrics/seed-demo', { days: seedDays });
        // Usa o valor REAL retornado: dias com dado real são pulados, então
        // days_created pode ser < seedDays.
        const criados = res.data?.days_created ?? 0;
        setDemoMessage(
          criados > 0
            ? `${criados} dia(s) de demonstração criado(s).`
            : 'Nenhum dia criado (já havia dados no período).',
        );
        await fetch();
      } catch (err) {
        setDemoMessage(
          mapError(err) === 'sem_permissao'
            ? 'Sem permissão para popular dados de teste.'
            : 'Falha ao popular dados de teste.',
        );
      } finally {
        setDemoLoading(false);
      }
    },
    [fetch],
  );

  // Remove a população de demonstração e recarrega o dashboard.
  const clearDemo = useCallback(async () => {
    setDemoLoading(true);
    setDemoMessage(null);
    try {
      const res = await waApi.delete('/dashboard/metrics/seed-demo');
      const removidos = res.data?.deleted ?? 0;
      setDemoMessage(
        removidos > 0
          ? `${removidos} dia(s) de demonstração removido(s).`
          : 'Nenhum dado de demonstração encontrado para remover.',
      );
      await fetch();
    } catch (err) {
      setDemoMessage(
        mapError(err) === 'sem_permissao'
          ? 'Sem permissão para remover dados de teste.'
          : 'Falha ao remover dados de teste.',
      );
    } finally {
      setDemoLoading(false);
    }
  }, [fetch]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return {
    data,
    isLoading,
    error,
    refetch: fetch,
    refresh,
    refreshing,
    refreshError,
    seedDemo,
    clearDemo,
    demoLoading,
    demoMessage,
  };
}
